/** Batch "Set status" dialog for the API-credentials grid (a custom
 *  `dialog(ctx)` batch action) — enable / disable / revoke selected
 *  credentials. Revoked rows in the selection are skipped with a per-row
 *  error (revocation is terminal), and revoking cascades to jwt_refresh
 *  children.
 */
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useState } from 'react';

import type { ApiCredentialStatus } from '@/api/apiCredentials';
import type { BatchActionContext } from '@/components/DataGrid/batchTypes';

const STATUS_CHOICES: { value: ApiCredentialStatus; label: string }[] = [
  { value: 'active', label: 'Active — the key authenticates' },
  { value: 'disabled', label: 'Disabled — rejected until re-enabled (reversible)' },
  { value: 'revoked', label: 'Revoked — terminal; also revokes refresh tokens' },
];

export function SetStatusBatchDialog({ ctx }: { ctx: BatchActionContext }) {
  const [status, setStatus] = useState<ApiCredentialStatus>('disabled');

  const submit = () => {
    void ctx.runStandard({ status });
    ctx.close();
  };

  return (
    <Dialog open onClose={ctx.close} fullWidth maxWidth="sm">
      <DialogTitle>Set credential status</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel id="set-status-label">Status</InputLabel>
            <Select
              labelId="set-status-label"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ApiCredentialStatus)}
            >
              {STATUS_CHOICES.map((c) => (
                <MenuItem key={c.value} value={c.value}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Already-revoked credentials are skipped with a per-row error — revocation is
              terminal.
            </FormHelperText>
          </FormControl>

          {status === 'revoked' && (
            <Alert severity="warning">
              Revocation can&apos;t be undone: the keys stop authenticating immediately and their
              refresh tokens are revoked with them.
            </Alert>
          )}

          {ctx.selection.mode === 'all_matching' && (
            <Alert severity="info">Acting on {ctx.matchedPhrase} credentials.</Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={ctx.close}>Cancel</Button>
        <Button
          variant="contained"
          color={status === 'revoked' ? 'error' : 'primary'}
          onClick={submit}
        >
          Set status for {ctx.count.toLocaleString()} credential(s)
        </Button>
      </DialogActions>
    </Dialog>
  );
}
